import { Dialog, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useContext, useMemo } from 'react';
import * as yup from 'yup';
import _ from 'lodash';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { CalendarEventRequiredProperty, CalendarEventRequiredPropertys, agendaSettingsCommand_updateRequiredProperties } from 'gen/ApiClient';
import { RecordContext } from 'shared/records/RecordContext';
import { handleValidateResponse } from 'framework/forms/utils/handleValidateResponse';
import { StepsField } from 'shared/process/StepsField';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';

interface IModel {
	properties: string[];
}

const createSchema = (strings: IStrings) => {
	return yup
		.object<IModel>({
			properties: yup.array().of(yup.string().defined()).defined(),
		})
		.defined();
};

interface IProps extends DialogProps {
	confirm: VoidFunction;
	cancel: VoidFunction;
	requiredProperties: string[];
}

export const CalendarEventRequiredPropertiesForm = ({ confirm, cancel, requiredProperties, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(agendaSettingsCommand_updateRequiredProperties);
	const { calendarEventRequiredPropertiesRecord } = useContext(RecordContext);
	const orderedProps = useMemo(
		() => _.orderBy(CalendarEventRequiredPropertys, t => calendarEventRequiredPropertiesRecord[t]),
		[calendarEventRequiredPropertiesRecord]
	);

	const handleSubmit = async (values: IModel, helpers: FormikHelpers<IModel>) => {
		const r = await update(values.properties);
		if (handleValidateResponse(r, helpers, 'properties')) {
			notify(strings.updated);
			confirm();
		}
	};

	return (
		<Formik<IModel>
			validateOnMount
			initialValues={{ properties: requiredProperties }}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					{...rest}
					scroll='paper'
					maxWidth='sm'
					fullWidth>
					<DialogTitle>
						<div>{strings.changeSomething(strings.requiredProperties)}</div>
					</DialogTitle>
					<DialogContent
						dividers
						style={{ paddingTop: 0, paddingBottom: 0 }}>
						<StepsField<IModel, CalendarEventRequiredProperty>
							steps={orderedProps}
							name='properties'
							record={calendarEventRequiredPropertiesRecord}
							isOptional={t => true}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions<IModel>
						cancel={cancel}
						submitText={strings.changeSomething(strings.requiredProperties)}
						isSubmitting={isSubmitting}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};
